import { useCallback, useEffect, useRef, useState } from 'react';

export const POSTER_LOAD_TIMEOUT_MS = 15000;
const POSTER_REQUEST_FALLBACK_MS = 2500;

const normalizePosterSource = (source) => String(source || '').trim();

export default function usePosterImageState(source) {
  const posterSource = normalizePosterSource(source);
  const hostRef = useRef(null);
  const loadTimeoutRef = useRef(null);
  const requestFallbackRef = useRef(null);
  const [shouldRequest, setShouldRequest] = useState(false);
  const [status, setStatus] = useState(posterSource ? 'waiting' : 'failed');

  const clearLoadTimeout = useCallback(() => {
    if (loadTimeoutRef.current == null) return;
    window.clearTimeout(loadTimeoutRef.current);
    loadTimeoutRef.current = null;
  }, []);

  const clearRequestFallback = useCallback(() => {
    if (requestFallbackRef.current == null) return;
    window.clearTimeout(requestFallbackRef.current);
    requestFallbackRef.current = null;
  }, []);

  const requestPoster = useCallback(() => {
    if (!posterSource) return;
    clearRequestFallback();
    setStatus((current) => (current === 'loaded' || current === 'failed' ? current : 'loading'));
    setShouldRequest(true);
  }, [clearRequestFallback, posterSource]);

  useEffect(() => {
    clearLoadTimeout();
    clearRequestFallback();
    setShouldRequest(false);
    setStatus(posterSource ? 'waiting' : 'failed');
  }, [clearLoadTimeout, clearRequestFallback, posterSource]);

  useEffect(() => {
    if (!posterSource) return undefined;

    const host = hostRef.current;
    const Observer = typeof window !== 'undefined' ? window.IntersectionObserver : null;

    if (!host || typeof Observer !== 'function') {
      requestPoster();
      return undefined;
    }

    const observer = new Observer((entries) => {
      if (!entries.some((entry) => entry.isIntersecting || entry.intersectionRatio > 0)) return;
      requestPoster();
      observer.disconnect();
    }, {
      root: null,
      rootMargin: '700px 0px',
      threshold: 0.01,
    });

    observer.observe(host);
    requestFallbackRef.current = window.setTimeout(requestPoster, POSTER_REQUEST_FALLBACK_MS);

    return () => {
      observer.disconnect();
      clearRequestFallback();
    };
  }, [clearRequestFallback, posterSource, requestPoster]);

  useEffect(() => {
    if (!posterSource || !shouldRequest) return undefined;

    clearLoadTimeout();
    loadTimeoutRef.current = window.setTimeout(() => {
      loadTimeoutRef.current = null;
      setStatus((current) => (current === 'loaded' ? current : 'failed'));
    }, POSTER_LOAD_TIMEOUT_MS);

    return clearLoadTimeout;
  }, [clearLoadTimeout, posterSource, shouldRequest]);

  useEffect(() => () => {
    clearLoadTimeout();
    clearRequestFallback();
  }, [clearLoadTimeout, clearRequestFallback]);

  const handleLoad = useCallback(() => {
    clearLoadTimeout();
    setStatus('loaded');
  }, [clearLoadTimeout]);

  const handleError = useCallback(() => {
    clearLoadTimeout();
    setStatus('failed');
  }, [clearLoadTimeout]);

  const hasSource = Boolean(posterSource);
  const isFailed = !hasSource || status === 'failed';

  return {
    hostRef,
    posterSource,
    shouldRequest: hasSource && shouldRequest && !isFailed,
    isLoaded: status === 'loaded',
    isLoading: hasSource && status !== 'loaded' && !isFailed,
    isFailed,
    handleLoad,
    handleError,
  };
}
